/**
 * Service for initializing template project structure and files
 */

import api from './api';

export interface TemplateProjectInitializationOptions {
  conversationId: string;
  workingDirectory?: string;
  initGit?: boolean;
  retryOnGitError?: boolean;
}

export interface TemplateProjectStructure {
  projectId: string;
  directories: string[];
  files: Array<{
    path: string;
    content: string;
  }>;
}

/**
 * Generate initial state.json content for template project
 */
function generateInitialTemplateState(projectId: string): string {
  const now = new Date().toISOString();

  const initialState = {
    id: projectId,
    type: 'template',
    project_title: projectId, // Initially use projectId, will be updated by LLM
    thumbnail: null,
    creation_date: now,
    status: 'in_progress',
    workflow: {
      validation: {
        status: 'pending',
        validation_file: null,
        layout_count: null,
        total_assets: null,
        assets_by_type: {}
      },
      layout_generation: {
        status: 'pending',
        layouts_generated: [],
        total_layouts: null,
        completed_layouts: null
      },
      design_document: {
        status: 'pending',
        document_file: null,
        sections: [],
        word_count: null
      }
    },
    assets: {
      images: [],
      fonts: [],
      documents: [],
      other: []
    },
    // IMPORTANT: Always null - never expose absolute paths to LLM
    working_directory: null,
    project_metadata: {
      working_directory: null,
      project_root: null,
      state_file_path: null
    }
  };

  return JSON.stringify(initialState, null, 2);
}

/**
 * Create project structure for template creation
 */
export function createTemplateProjectStructure(projectId: string): TemplateProjectStructure {
  const directories = [
    'resources',
    'resources/images',
    'resources/fonts',
    'resources/documents',
    'resources/other',
    'layouts'
  ];

  const files = [
    {
      path: 'state.json',
      content: generateInitialTemplateState(projectId)
    },
    {
      path: 'validation.json',
      content: JSON.stringify({
        layout_count: 0,
        total_assets: 0,
        assets_by_type: {
          images: 0,
          fonts: 0,
          documents: 0,
          other: 0
        },
        assets: [],
        recommendations: '',
        validated_at: null
      }, null, 2)
    },
    {
      path: 'design_document.md',
      content: `# Design System Documentation

## Overview

This document will be automatically generated by the template workflow to provide comprehensive design system documentation.

---

*Generated by Template Workflow - This placeholder will be replaced during the design document generation step.*

## Sections

The design document will include:

1. **Overview**: Introduction to the template system
2. **Color Palette**: Extracted color schemes and usage guidelines
3. **Typography**: Font families, sizes, weights, and hierarchy
4. **Template Layouts**: Detailed documentation of each layout template
5. **Components**: Reusable component patterns and usage
6. **Asset Inventory**: Complete catalog of images, fonts, and resources
7. **Usage Guidelines**: Best practices for using the template system
8. **Technical Specifications**: Implementation details and requirements

---

**Status**: Pending design document generation

**Last Updated**: ${new Date().toISOString()}
`
    },
    {
      path: 'README.md',
      content: `# Template Project: ${projectId}

## Project Structure

\`\`\`
${projectId}/
├── resources/           # Uploaded template resources
│   ├── images/         # Image assets (PNG, JPG, SVG, etc.)
│   ├── fonts/          # Font files (TTF, OTF, WOFF, etc.)
│   ├── documents/      # Document assets (PDF, DOCX, etc.)
│   └── other/          # Other resource files
├── layouts/            # Generated HTML template layouts
├── state.json          # Project state and workflow status
├── validation.json     # Asset validation results
└── design_document.md  # Comprehensive design system documentation
\`\`\`

## Workflow

### Step 1: Validation
- Analyzes uploaded resources
- Creates asset inventory
- Determines layout count
- Generates recommendations

### Step 2: Layout Generation
- Creates HTML template layouts
- Generates layout metadata (JSON)
- Organizes layouts in \`layouts/\` directory

### Step 3: Design Document
- Extracts design system documentation
- Documents color palettes, typography, components
- Provides usage guidelines

## Status

Check \`state.json\` for current workflow status and progress.

---

**Created**: ${new Date().toISOString()}
`
    }
  ];

  return {
    projectId,
    directories,
    files
  };
}

/**
 * Template Project Initialization Service
 */
export const templateProjectInitializationService = {
  /**
   * Initialize a new template project structure
   * Note: The actual initialization happens server-side when creating a style-conversations conversation
   * This method just returns the expected structure for consistency with video workflow
   */
  async initializeProject(options: TemplateProjectInitializationOptions): Promise<TemplateProjectStructure> {
    const projectId = options.conversationId; // Use conversation ID as project ID
    const projectStructure = createTemplateProjectStructure(projectId);

    console.log(`✅ Template project structure prepared for conversation: ${projectId}`);

    // The actual initialization happens in the conversation controller
    // when collectionType: 'STYLE' is used
    // So we just return the structure for reference
    return projectStructure;
  },

  /**
   * Check if a conversation has an initialized template project
   */
  async checkProjectStatus(conversationId: string): Promise<{
    initialized: boolean;
    projectId?: string;
    workingDirectory?: string;
    projectType?: 'template';
  }> {
    try {
      // Check via conversation endpoint
      const response = await api.get(`/conversations/${conversationId}`);
      const conversation = response.data.conversation;

      if (conversation.collectionType === 'STYLE') {
        return {
          initialized: true,
          projectId: conversationId,
          workingDirectory: conversation.metadata?.workingDirectory,
          projectType: 'template'
        };
      }

      return { initialized: false };
    } catch (error) {
      console.error('Failed to check template project status:', error);
      return { initialized: false };
    }
  }
};
